import * as React from "react"
import { DayPicker, getDefaultClassNames, useDayPicker, type DayButton } from "react-day-picker"

import { cn } from "~/utils/helpers"
import { Button, IconButton } from "~/components/reusable/Button"
import { CaretLeft, CaretRight, CaretDown } from "@phosphor-icons/react"
import type { Locale } from "react-day-picker"

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  captionLayout = "label",
  buttonVariant = "neutral",
  locale,
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "[--cell-radius:8px] [--cell-size:--spacing(7)] bg-background group/calendar in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("flex gap-4 flex-col md:flex-row relative", defaultClassNames.months),
        month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
        nav: cn(
          "flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariant === "neutral" ? "bg-base hover:bg-base-hover" : "",
          " aria-disabled:opacity-50 p-1.5 select-none hover:cursor-pointer hover:text-fg-subtle rounded-lg flex items-center justify-center",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariant === "neutral" ? "bg-base hover:bg-base-hover" : "",
          " aria-disabled:opacity-50 p-1.5 select-none hover:cursor-pointer hover:text-fg-subtle rounded-lg flex items-center justify-center",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex items-center justify-center h-(--cell-size) w-full",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "relative text-fg-base rounded-(--cell-radius) px-1 py-1 hover:bg-base-hover cursor-pointer",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          "absolute inset-0 opacity-0 [&>option]:bg-base [&>option]:text-fg-base",
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          "select-none font-medium text-fg-base",
          captionLayout === "label"
            ? "text-sm"
            : "rounded-(--cell-radius) flex items-center gap-1.5 text-sm [&>svg]:text-fg-muted",
          defaultClassNames.caption_label,
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex gap-2", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground rounded-(--cell-radius) w-(--cell-size) shrink-0 text-center font-normal text-[0.8rem] select-none",
          defaultClassNames.weekday,
        ),
        week: cn("flex mt-2 gap-2", defaultClassNames.week),
        week_number_header: cn("select-none w-(--cell-size)", defaultClassNames.week_number_header),
        week_number: cn(
          "text-[0.8rem] select-none text-muted-foreground",
          defaultClassNames.week_number,
        ),
        day: cn(
          "relative w-(--cell-size) h-(--cell-size) shrink-0 p-0 text-center group/day select-none",
          defaultClassNames.day,
        ),
        range_start: cn(defaultClassNames.range_start),
        range_middle: cn(defaultClassNames.range_middle),
        range_end: cn(defaultClassNames.range_end),
        today: "rounded-(--cell-radius)",
        outside: cn(
          "text-fg-disabled aria-selected:text-fg-disabled bg-transparent! hover:bg-transparent!",
          defaultClassNames.outside,
        ),
        disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
        },
        Nav: ({ className, children, ...props }) => {
          return (
            <div className={cn(className)} {...props}>
              {children}
            </div>
          )
        },
        MonthCaption: ({ ...captionProps }) => <CalendarMonthCaption {...captionProps} />,
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return <CaretLeft className={cn("size-4", className)} {...props} />
          }

          if (orientation === "right") {
            return <CaretRight className={cn("size-4", className)} {...props} />
          }

          return <CaretDown className={cn("size-4", className)} {...props} />
        },
        DayButton: ({ ...props }) => <CalendarDayButton locale={locale} {...props} />,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

const navButtonCss =
  "p-1.5 rounded-lg hover:bg-base-hover hover:cursor-pointer hover:text-fg-subtle text-fg-muted flex items-center justify-center select-none aria-disabled:opacity-50"

function CalendarMonthCaption({
  children,
  ...props
}: {
  children?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>) {
  const { goToMonth, previousMonth, nextMonth } = useDayPicker()

  return (
    <div className="flex items-center justify-between h-(--cell-size) w-full" {...props}>
      <IconButton
        variant="transparent"
        size="sm"
        className={navButtonCss}
        disabled={!previousMonth}
        aria-disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
      >
        <CaretLeft className="size-4" />
      </IconButton>
      <span className="select-none text-sm font-medium w-full text-center">{children}</span>
      <IconButton
        variant="transparent"
        size="sm"
        className={navButtonCss}
        disabled={!nextMonth}
        aria-disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      >
        <CaretRight className="size-4" />
      </IconButton>
    </div>
  )
}

const selectedCss =
  "data-[selected-single=true]:bg-interactive data-[selected-single=true]:text-fg-on-primary data-[selected-single=true]:rounded-(--cell-radius) data-[selected-single=true]:hover:bg-interactive"

const middleCss =
  "data-[range-middle=true]:bg-background-highlight data-[range-middle=true]:hover:bg-background-highlight-hover data-[range-middle=true]:text-fg-interactive data-[range-middle=true]:rounded-(--cell-radius)"

const startCss =
  "data-[range-start=true]:bg-interactive data-[range-start=true]:text-fg-on-primary data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:hover:bg-interactive"

const endCss =
  "data-[range-end=true]:bg-interactive data-[range-end=true]:text-fg-on-primary data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:hover:bg-interactive"

const todayBaseCss =
  "data-[today=true]:after:absolute data-[today=true]:after:bottom-1 data-[today=true]:after:left-1/2 data-[today=true]:after:-translate-x-1/2 data-[today=true]:after:size-1 data-[today=true]:after:rounded-full"

const outsideCss =
  "data-[outside=true]:text-fg-muted data-[outside=true]:bg-transparent! data-[outside=true]:hover:bg-transparent! data-[outside=true]:pointer-events-none"

const focusedCss =
  "group-data-[focused=true]/day:bg-interactive group-data-[focused=true]/day:text-fg-on-primary"

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="transparent"
      size="sm"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        !modifiers.outside &&
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-today={modifiers.today}
      data-outside={modifiers.outside}
      data-range-start={!modifiers.outside && modifiers.range_start}
      data-range-end={!modifiers.outside && modifiers.range_end}
      data-range-middle={!modifiers.outside && modifiers.range_middle}
      className={cn(
        "relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 rounded-(--cell-radius) border-0 leading-none font-normal hover:bg-base-hover [&>span]:text-xs [&>span]:opacity-70",
        selectedCss,
        middleCss,
        startCss,
        endCss,
        todayBaseCss,
        modifiers.today &&
          (!modifiers.outside &&
          (modifiers.selected ||
            modifiers.range_start ||
            modifiers.range_end ||
            modifiers.range_middle)
            ? "after:bg-fg-on-primary"
            : "after:bg-fg-interactive"),
        outsideCss,
        focusedCss,
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
